import type { RegionEntitlementDeniedBody } from "@certn/api";
import { useRequest } from "@certn/api";
import { PrimaryButton, SectionMessage, Stack } from "@certn/cascadia-react";
import { useState } from "react";
import { Trans, useTranslation } from "react-i18next";

import { EmptyState } from "./EmptyState";
import { RegionEntitlementDeniedLayout } from "./RegionEntitlementDeniedLayout";
import { RegionEntitlementSignUpForm } from "./RegionEntitlementSignUpForm";
import {
  exitProxySessionAndRedirect,
  type PurgeProtectedCacheFn,
} from "../utils/proxyExit";

interface Props {
  denialBody: RegionEntitlementDeniedBody;
  onSignupSuccess: () => Promise<void>;
  purgeProtectedCache: PurgeProtectedCacheFn;
}

export function RegionEntitlementDeniedPage(props: Props) {
  const { denialBody, onSignupSuccess, purgeProtectedCache } = props;
  const { t } = useTranslation();
  const request = useRequest();
  const [showSignUpRegionPage, setShowSignUpRegionPage] = useState(false);
  const [proxyExitError, setProxyExitError] = useState<string | undefined>();

  const handleProxyExit = async () => {
    setProxyExitError(undefined);
    try {
      await exitProxySessionAndRedirect(request, purgeProtectedCache);
    } catch {
      setProxyExitError(t("Something went wrong, try again"));
    }
  };

  if (showSignUpRegionPage && denialBody.can_create_client_account) {
    return (
      <RegionEntitlementSignUpForm
        onClose={() => setShowSignUpRegionPage(false)}
        onSuccess={async () => {
          await onSignupSuccess().catch(() => undefined);
        }}
      />
    );
  }

  return (
    <RegionEntitlementDeniedLayout>
      <EmptyState
        title={t("No Access")}
        focusTitle
        subtitle={
          <span id="region-denied-message">
            {t("This account cannot access Client Portal in this region.")}
          </span>
        }
        illustration="verification-fail"
        actions={
          <Stack orientation="vertical" gap="space-2">
            {proxyExitError && (
              <SectionMessage type="danger" sectionTitle={t("System Error")}>
                {proxyExitError}
              </SectionMessage>
            )}
            {denialBody.can_create_client_account && (
              <PrimaryButton
                type="button"
                variant="info"
                testID="region-denied-create-account"
                onPress={() => setShowSignUpRegionPage(true)}
              >
                {t("Sign up to new region")}
              </PrimaryButton>
            )}
            {denialBody.can_exit_proxy && (
              <PrimaryButton
                type="button"
                variant="info"
                testID="region-denied-exit-proxy"
                onPress={handleProxyExit}
              >
                <Trans>Return to Case Management</Trans>
              </PrimaryButton>
            )}
          </Stack>
        }
      />
    </RegionEntitlementDeniedLayout>
  );
}
